import * as React from 'react';
import { useColorScheme, alpha, type Theme } from '@mui/material/styles';
import { Box, IconButton, Stack, Switch, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import { getButtonColor, getIconColor, getIconHoverColor, getIconLightColor } from '../../theme/themeUtils';
import { responsiveFontSize, responsiveIconSize, StackedActionButton } from '../../util/responsiveUtils';

import DarkModeRoundedIcon from '@mui/icons-material/DarkModeRounded';
import LightModeRoundedIcon from '@mui/icons-material/LightModeRounded';

export function ColorSchemeToggle() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { setMode } = useColorScheme();
  const isDark = theme.palette.mode === 'dark';

  // Use utility functions to get icon colors
  const iconColor = getIconColor(theme);
  const hoverColor = getIconHoverColor(theme);
  const hoverBackground = alpha(getIconLightColor(theme), 0.2);

  return (
    <Tooltip title="Change theme">
      <IconButton
        data-screenshot="toggle-mode"
        size={isMobile ? "small" : "medium"}
        color={isDark ? undefined : 'primary'}
        sx={{
          alignSelf: 'center',
          p: { xs: 0.5, sm: 1 },
          ...(isDark && { color: iconColor }),
          '@media (hover: hover)': {
            '&:hover': {
              color: hoverColor,
              backgroundColor: hoverBackground
            }
          }
        }}
        onClick={() => setMode(isDark ? 'light' : 'dark')}
      >
        {isDark ? (
          <LightModeRoundedIcon sx={{ fontSize: responsiveIconSize.sm }} />
        ) : (
          <DarkModeRoundedIcon sx={{ fontSize: responsiveIconSize.sm }} />
        )}
      </IconButton>
    </Tooltip>
  );
}

const menuToggleRowSx = (theme: Theme) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 2,
  minHeight: StackedActionButton.MIN_HEIGHT,
  py: { xs: 1.25, sm: 1.5 },
  px: { xs: 2, sm: 2.5 },
  borderBottom: `1px solid ${theme.palette.divider}`,
});

/** Theme light/dark switch for use inside menus (e.g. header hamburger). */
export function ColorSchemeMenuToggle() {
  const theme = useTheme();
  const { setMode } = useColorScheme();
  // palette.mode reflects the applied scheme (including system preference on first visit).
  const isDark = theme.palette.mode === 'dark';

  const row = (
    <>
      <Stack direction="row" alignItems="center" gap={{ xs: 1.5, sm: 2 }} sx={{ minWidth: 0 }}>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: 2,
            backgroundColor: alpha(getButtonColor(theme), 0.12),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              color: getButtonColor(theme),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              '& .MuiSvgIcon-root': { fontSize: responsiveIconSize.sm },
            }}
          >
            {isDark ? <LightModeRoundedIcon /> : <DarkModeRoundedIcon />}
          </Box>
        </Box>
        <Typography
          component="label"
          htmlFor="menu-color-scheme-switch"
          sx={{
            fontWeight: 500,
            fontSize: responsiveFontSize.bodyMenu,
            color: 'text.primary',
            cursor: 'pointer',
          }}
        >
          Dark mode
        </Typography>
      </Stack>
      <Switch
        id="menu-color-scheme-switch"
        checked={isDark}
        onChange={(_, checked) => setMode(checked ? 'dark' : 'light')}
        inputProps={{ 'aria-label': 'Dark mode', 'data-screenshot': 'toggle-mode' } as React.InputHTMLAttributes<HTMLInputElement>}
        sx={{ flexShrink: 0 }}
      />
    </>
  );

  return <Box sx={menuToggleRowSx(theme)}>{row}</Box>;
}
